import { observer } from 'mobx-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  AccountsPayableLinkStatus as APStatus,
  accountsPayableLinkStep,
} from '@constants/accountsPayableLink';

import { AccountsPayableRouteParams } from '@containers/types';
import useAccountsPayable from '@hooks/useAccountsPayable';
import useStore from '@hooks/useStore';
import GuestLayout from '@layouts/guest';

import Validation from './Validation';
import FundingApprovalRequest from './FundingApprovalRequest';
import OperationInProgress from './OperationInProgress';
import OperationCompleted from './OperationCompleted';
import CanceledAccountsPayable from './Canceled';
import Stepper from './Stepper';

import useStyles from '@containers/AccountsPayable/styles';

export interface IAccountsPayable {}

const AccountsPayable: FC<IAccountsPayable> = () => {
  const css = useStyles();
  const { t } = useTranslation();
  const { accountsPayableLink } = useStore();
  const { status, flags } = accountsPayableLink;
  const { isRequesting } = flags;
  const params = useParams<AccountsPayableRouteParams>();
  const accountsPayableLinkId = params.accountsPayableLinkId;
  const [loading, setLoading] = useState(true);
  const { handleError } = useAccountsPayable();
  const DELAY = 2000;

  const isLoading = useMemo(() => isRequesting || loading, [isRequesting, loading]);

  const fetchData = async () => {
    try {
      await accountsPayableLink.getAccountsPayableLink(accountsPayableLinkId);
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  useEffect(() => {
    if (accountsPayableLinkId) {
      const timeout = setTimeout(() => {
        setLoading(false);
        fetchData();
      }, DELAY);

      return () => clearTimeout(timeout);
    }
  }, [accountsPayableLinkId]);

  const Components = [
    { component: <Validation accountsPayableLinkId={accountsPayableLinkId} /> },
    { component: <FundingApprovalRequest /> },
    { component: <OperationInProgress /> },
    { component: <OperationCompleted /> },
  ];

  return (
    <GuestLayout loading={isLoading} isAccountsPayable>
      <>
        <div className={css.mainContainer}>
          {status && status !== APStatus.CANCELED ? (
            <>
              {status === APStatus.ACTIVE && (
                <span className={css.title}>{t('accountsPayable.welcome')}</span>
              )}
              {status && <Stepper />}
              {Components[accountsPayableLinkStep[status]]?.component}
            </>
          ) : (
            <CanceledAccountsPayable />
          )}
        </div>
      </>
    </GuestLayout>
  );
};

export default observer(AccountsPayable);
